/**
 * UTM utilities — capture from URL on landing, persist in cookies, attach to leads.
 *
 * Behavior (atomic refresh for landing attribution):
 * - On landing, if the URL has any UTM, ALL previous UTM cookies are cleared
 *   and the new ones are stored. This avoids mixing campaigns.
 * - If the URL has no UTM and also has no `unidad` / `unit`, previous UTM
 *   cookies are cleared on a new landing. Internal SPA navigation preserves them.
 * - If the URL only carries `unidad` / `unit`, existing UTMs are preserved so
 *   detail/contact flows can still use the attribution captured on landing.
 * - Cookies live `UTM_COOKIE_DAYS` days, refreshed each time new UTMs arrive.
 * - `getUtms()` reads them back as a flat object (only present keys).
 */

export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

export const UTM_COOKIE_DAYS = 30;
export const UNIT_KEYS = ['unidad', 'unit'];

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const setCookie = (name, value, days) => {
  if (!isBrowser()) return;
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  // SameSite=Lax keeps cookies on top-level navigations (typical landing flow)
  document.cookie = `${name}=${encodeURIComponent(value)}; ${expires}; path=/; SameSite=Lax`;
};

const deleteCookie = (name) => {
  if (!isBrowser()) return;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
};

const readCookie = (name) => {
  if (!isBrowser()) return null;
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  if (!match) return null;
  const raw = match.slice(name.length + 1);
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

/**
 * Read UTMs from a search string (e.g. window.location.search).
 * Returns an object containing only present, non-empty UTM keys.
 */
export const parseUtmsFromSearch = (search) => {
  if (!search) return {};
  const params = new URLSearchParams(search);
  const out = {};
  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v && v.trim()) out[key] = v.trim();
  }
  return out;
};

export const hasUnitParam = (search) => {
  if (!search) return false;
  const params = new URLSearchParams(search);

  return UNIT_KEYS.some((key) => {
    const value = params.get(key);
    return Boolean(value && value.trim());
  });
};

// Native internal links reload the document, while React links do not.
export const isInternalLeadNavigation = () => {
  if (!isBrowser() || !document.referrer) return false;
  try { return new URL(document.referrer).origin === window.location.origin; }
  catch { return false; }
};

const clearStoredUtms = () => {
  UTM_KEYS.forEach(deleteCookie);
};

/**
 * If the current URL carries any UTM, clear previous UTM cookies and store
 * the incoming set. If the URL has neither UTM params nor unidad/unit, any
 * previously stored UTM cookies are cleared unless preserveStored is enabled for internal navigation.
 */
export const captureUtmsFromUrl = (search = isBrowser() ? window.location.search : '', { preserveStored = false } = {}) => {
  const incoming = parseUtmsFromSearch(search);
  if (Object.keys(incoming).length === 0) {
    if (!hasUnitParam(search) && !preserveStored) {
      clearStoredUtms();
    }

    return null;
  }

  // Atomic replace: clear all previous UTM cookies first
  clearStoredUtms();

  // Persist incoming UTMs
  Object.entries(incoming).forEach(([k, v]) => setCookie(k, v, UTM_COOKIE_DAYS));

  return incoming;
};

/**
 * Read current URL attribution first, otherwise the stored landing attribution.
 */
export const getUtms = (search = isBrowser() ? window.location.search : '') => {
  const incoming = parseUtmsFromSearch(search);

  if (Object.keys(incoming).length > 0) {
    return incoming;
  }


  const out = {};
  for (const key of UTM_KEYS) {
    const v = readCookie(key);
    if (v) out[key] = v;
  }
  return out;
};
